import { MultiExpando } from "../widgets/MultiExpando";
import { Say } from "../widgets/Say";
import { Say2 } from "../widgets/Say2";

/** help on how to use CustomSets, the UI for picking custom vowels for English lexical sets */
export function HelpCustomSets() {
  return (<>
    Pick topics for help:&nbsp;
    <MultiExpando labels={['save/load', 'customizing vowels', 'sample text', 'tips']}>{[
      <SaveLoad key={1} />, <Vowels key={2} />, <Sample key={3} />, <Tips key={4} />,
    ]}</MultiExpando>
  </>);
}

function SaveLoad() {
  return (<>
    <h3>Saving, loading, and deleting custom accents</h3>

    <p>
      To create a custom accent, start by picking a reference accent from the <i>get sets from</i> list.
      Then type a name into the <i>accent name</i> box and start customizing the vowels
      for any of the lexical sets you want to tweak.
      Your custom accents will show up at the end of the <i>get sets from</i> list,
      so they can be used to derive new accents as well.
    </p>

    <ul>
      <li>To immediately create a new accent from the current set of vowels, type in an <i>accent name</i> and press enter.</li>
      <li>Whenever there's a name in the <i>accent name</i> box, all changes to vowels will be immediately saved.</li>
      <li>When there <i>isn't</i> a name in the <i>accent name</i> box, changes are <i>not</i> saved, allowing you to experiment without changing the accent.</li>
      <li>To get rid of a custom accent, pick the <i>delete</i> button when its name appears in the <i>accent name</i> box.</li>
    </ul>
  </>);
}

function Vowels() {
  return (<>
    <h3>Customizing Vowels</h3>

    <p>
      The table along the left-hand side shows the standard lexical sets,
      along with the corresponding vowels and lengths (in milliseconds) for the current accent.
      The chart shows the vowel(s) for the currently selected lexical set in red.
      Pick the <i>show all sets</i> option to have the chart show the vowels for every lexical set in green.
      See <a href="?page=speech-essay#vowels">vowels</a> for more information on the vowel chart.
    </p>

    <p>
      Each vowel description consists of one to four pieces:
    </p>

    <ul>
      <li>
        A vowel - such as <Say say="/i/">i</Say> -&nbsp;
        which may optionally be marked as <i>long</i> - such as <Say say="/iː/">iː</Say> -&nbsp;
        or with a tilde to indicate it's <i>nasal</i> - such as <Say say="/ĩ/">ĩ</Say>.
      </li>
      <li>
        [optional] A second vowel with a mark below it to indicate it's part of a <i>diphthong</i>
        &nbsp;- such as <Say>eɪ̯</Say>.
      </li>
      <li>
        [optional] A final r-sound - such as the <Say2>ɹ</Say2> in <Say say="/ɑɹ/">ɑɹ</Say> or
        the <Say2>ɾ</Say2> in <Say say="/aə̯ɾ/">aə̯ɾ</Say>.
      </li>
      <li>
        [optional] A middle vowel in a <i>triphthong</i> - such as <Say say="/æɪ̯ə̯/">æɪ̯ə̯</Say> in <Say say="/ˈtɹæɪ̯ə̯220p/">trap</Say>.
      </li>
    </ul>

    <p>
      There are several different ways to change the vowel(s) associated with a lexical set:
    </p>

    <ul>
      <li>
        Click on the name of the lexical set to select it.
        By default, you can then simply click on one of the vowels in the vowel chart to switch the set to using that vowel.
        Or pick <i>first</i>, <i>middle</i>, or <i>last</i> from the options below the chart
        so that picking in the vowel chart will modify the vowel in that position of the monothong, diphthong, or triphthong.
      </li>
      <li>
        Or, you can pick the middle or last piece of the vowel from one of the lists below the chart.
      </li>
      <li>
        Or, you can double-click on the lexical set's vowels in order to edit them by typing.
      </li>
      <li>
        Or you can change the first vowel to long or nasal by checking the appropriate item.
      </li>
    </ul>

    <p>
      Note that adding the <i>long</i> symbol doesn't actually change the length of the vowel.
      What it does is allow you to independently change the length for sets that otherwise use the same vowel,
      since all instances of a vowel will use the same length for an accent.
      For example, say that you want PALM and LOT to both use <Say2>a</Say2>, but you want the vowel to be longer in PALM.
      If you have them both use <Say say="/a/">a</Say>, they will both be the same length.
      But if you switch PALM to <Say say="/aː/">aː</Say>, you can set it to a longer length.
    </p>

  </>);
}

function Sample() {
  return (<>
    <h3>Sample text</h3>

    <p>
      At the bottom of the interface is an area where you can try out your custom accent.
      Type English into the <i>sample text</i> box and pick the <i>speak</i> button to
      hear the words spoken in the current accent.
    </p>

    <p>
      Right below <i>sample text</i> is the <i>lexical sets</i> box,
      which will show you the lexical sets it inferred for the words you typed.
      For example, if you enter <i>speak</i>, it will show <i>ˈsp(FLEECE)k</i>.
      This means that it will substitute whatever vowel you entered for the FLEECE lexical set in place of <i>(FLEECE)</i>.
      For instance, if you set it to <Say>əi̯</Say>, you'd end up with <Say>/ˈspəi̯k/</Say>,
      which you can see in the <i>as IPA</i> box.
    </p>

    <p>
      This gives you the ability to tweak the pronunciation for any word.
      Type it in, see which lexical set(s) it uses, then change the vowel associated with that lexical set.
      Similar words will then share that sound.
    </p>
  </>);
}

function Tips() {
  return (<>
    <h3>Tips</h3>

    <p>
      Tips for picking vowels that work best with the built-in speech synthesizer:
    </p>
    <ul>
      <li>
        See <a href="?page=speech-essay">Picking Speech Phonemes</a> to understand the limitations of the speech synthesizer.
        It also includes a list of the built-in diphthongs.
      </li>
      <li>
        <Say2>ɹ</Say2> is the r-sound heard in many English accents, for example <Say say="/ˈɹɛd/">red /ˈɹɛd/</Say>.
        That symbol is also used as the last part of rhotic vowel sounds
        such as the <Say>/ɑɹ/</Say> in <Say say="/ˈstɑɹt/">start /ˈstɑɹt/</Say>.
      </li>
      <li>
        Alternate r-sounds used by some accents include <Say2>ɾ</Say2>, called a <i>flap</i>, or <Say2>r</Say2>, a <i>trill</i>.
      </li>
      <li>
        Triphthongs are three vowels spoken quickly together and are a bit more challenging for the speech synthesizer.
        Consider <Say say="/ˈbæɪ̯ə̯250θ/">bath /ˈbæɪ̯ə̯θ/</Say> or <Say say="/ˈdɹɛɪ̯ə̯s/">dress /ˈdɹɛɪ̯ə̯s/</Say> from some Southern US accents.
      </li>
    </ul>

    <p>
      General tips:
    </p>
    <ul>
      <li>
        If you want to design a custom accent that you can understand, it's best not to go too crazy by making big changes to every vowel.
        Often, an accent gets its flavor from a few subtle differences or maybe one or two larger changes.
      </li>
      <li>
        If you want try to emulate an existing accent,&nbsp;
        <a href="https://en.wikipedia.org/wiki/International_Phonetic_Alphabet_chart_for_English_dialects">lexical sets for English dialects</a>
        &nbsp;lists lexical set vowels for a variety of English accents.
      </li>
      <li>
        A natural accent is generally spread across the possible vowel space rather than clustered in one area.
        If you're creating a novel accent, it may be useful to turn on <i>show all sets</i> to
        see if your selected vowels are spread out or clustered in one portion of vowel space.
      </li>
    </ul>
  </>);
}
